import { Box, Grid } from "@mui/material";
import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { sidebarWidth } from "../../../../layout/components/sidebar/sidebar";
import { useShowSidebar } from "../../../../layout/hooks/use-show-sidebar";
import type { ToolbarProps } from "./props";
import { UndoRedoButtons } from "./undo-redo-buttons";

type Props = ToolbarProps & { rightAlignedElements: ReactNode; children: ReactNode };

export const ToolbarV2: FC<Props> = ({ disabled, children, rightAlignedElements }) => {
  const [isPinned, setIsPinned] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const showSidebar = useShowSidebar();

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const toolbar = toolbarRef.current;
    if (!sentinel || !toolbar || typeof window.IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Get scroll direction
        const scrollingDown = window.scrollY > lastScrollY.current;
        lastScrollY.current = window.scrollY;

        if (!entry!.isIntersecting && scrollingDown) setIsPinned(true);
        else if (entry!.isIntersecting) setIsPinned(false);
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <div
          ref={sentinelRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "1px",
            height: "1px",
            pointerEvents: "none",
            opacity: 0,
          }}
        />

        <Box
          ref={toolbarRef}
          id="toolbar"
          sx={{
            position: isPinned ? "fixed" : "static",
            top: isPinned ? 74 : "auto",
            left: 0,
            width: "100%",
            zIndex: 1000,
            padding: "2px",
            borderBottom: 1,
            borderBottomRightRadius: 1,
            borderBottomLeftRadius: 1,
            borderBottomStyle: "solid",
            borderBottomColor: "divider",
            backgroundColor: "background.paper",
            pl: isPinned && showSidebar ? `${sidebarWidth}px` : "auto",
          }}
        >
          <Grid container>
            <Grid style={{ flexGrow: "1" }}>{children}</Grid>
            <Grid sx={{ mr: 3 }}>{rightAlignedElements}</Grid>
            <Grid>
              <UndoRedoButtons disabled={disabled} />
            </Grid>
          </Grid>
        </Box>

        <div
          // eslint-disable-next-line react-hooks/refs
          style={{
            // eslint-disable-next-line react-hooks/refs
            height: isPinned ? toolbarRef.current?.offsetHeight : 0,
            display: isPinned ? "block" : "none",
          }}
        />
      </div>
    </div>
  );
};
